import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { BranchLimitControl } from "./BranchLimitControl";
import { useLocationConfiguration } from "./LocationConfiguration";
import { useLanguage } from "./Language";
import axios from "axios";
import { useBranchLimits } from "./BranchLimits";
import {
  AdvancedSearchEvent,
  SearchFieldKey,
  SearchTypeKey,
} from "./BooleanSearchForm";

type InterestLevel = "LG" | "MG" | "MG+" | "UG";

interface ReadingLevelInfo {
  readingLevelList: number[];
  interestLevelList: InterestLevel[];
  lexileList: number[];
}

export interface ReadingLevelSearchFormProps {
  onClickSearch: (event: AdvancedSearchEvent) => void;
}

export const ReadingLevelSearchForm: React.FC<ReadingLevelSearchFormProps> = (
  props
) => {
  const { onClickSearch } = props;
  const interestLevelHelpText: Record<InterestLevel, string> = {
    LG: "(K-3)",
    MG: "(4-8)",
    "MG+": "(6 and up)",
    UG: "(9-12)",
  };

  const { selectedBranchIds } = useBranchLimits();

  const lexileMinDefault = 5;
  const lexileMaxDefault = 2000;
  const lexileStep = 5;
  const arMinDefault = 0.0;
  const arMaxDefault = 13.0;
  const arStep = 0.1;

  const [searchTerm, setSearchTerm] = React.useState("");
  const [lexileMin, setLexileMin] = React.useState(lexileMinDefault);
  const [lexileMax, setLexileMax] = React.useState(lexileMaxDefault);
  const [lexile, setLexile] = React.useState([
    lexileMinDefault,
    lexileMaxDefault,
  ]);
  const [interestLevelList, setInterestLevelList] =
    React.useState<InterestLevel[]>(null);

  const [acceleratedReaderMin, setAcceleratedReaderMin] =
    React.useState(arMinDefault);
  const [acceleratedReaderMax, setAcceleratedReaderMax] =
    React.useState(arMaxDefault);
  const [acceleratedReader, setAcceleratedReader] = React.useState([
    arMinDefault,
    arMaxDefault,
  ]);

  const { locationConfiguration: { pacSettings } = { pacSettings: null } } =
    useLocationConfiguration();
  const { translate } = useLanguage();

  const handleSliderChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      setLexileMin(newValue[0]);
      setLexile([newValue[0], lexile[1]]);
    } else {
      setLexileMax(newValue[1]);
      setLexile([lexile[0], newValue[1]]);
    }
  };

  const handleARSliderChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      setAcceleratedReaderMin(newValue[0]);
      setAcceleratedReader([newValue[0], acceleratedReader[1]]);
    } else {
      setAcceleratedReaderMax(newValue[1]);
      setAcceleratedReader([acceleratedReader[0], newValue[1]]);
    }
  };

  const handleARMinimumChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const min =
      event.target.value === "" ? arMinDefault : Number(event.target.value);
    setAcceleratedReaderMin(min);
    setAcceleratedReader([min, acceleratedReaderMax]);
  };

  const handleARMaximumChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const max =
      event.target.value === "" ? arMaxDefault : Number(event.target.value);
    setAcceleratedReaderMax(max);
    setAcceleratedReader([acceleratedReaderMin, max]);
  };

  const handleMinimumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const lexileMin =
      event.target.value === "" ? lexileMinDefault : Number(event.target.value);
    setLexileMin(lexileMin);
    setLexile([lexileMin, lexileMax]);
  };

  const handleMinimumBlur = () => {
    if (lexileMin < lexileMinDefault) {
      setLexileMin(lexileMinDefault);
      setLexile([lexileMinDefault, lexileMax]);
    } else if (lexileMin > lexileMaxDefault) {
      setLexileMin(lexileMaxDefault);
      setLexile([lexileMaxDefault, lexileMax]);
    }
  };

  const handleMaximumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const max =
      event.target.value === "" ? lexileMaxDefault : Number(event.target.value);
    setLexileMax(max);
    setLexile([lexileMin, max]);
  };

  const handleMaximumBlur = () => {
    if (lexileMax < lexileMinDefault) {
      setLexileMax(lexileMinDefault);
      setLexile([lexileMin, lexileMinDefault]);
    } else if (lexileMax > lexileMaxDefault) {
      setLexileMax(lexileMaxDefault);
      setLexile([lexileMin, lexileMaxDefault]);
    }
  };

  const [interestLevel, setInterestLevel] = React.useState("");

  const handleChange = (event: SelectChangeEvent) => {
    setInterestLevel(event.target.value as string);
  };

  const handleClear = () => {
    setInterestLevel("");
    setAcceleratedReaderMin(arMinDefault);
    setAcceleratedReaderMax(arMaxDefault);
    setAcceleratedReader([arMinDefault, arMaxDefault]);
    setLexileMin(lexileMinDefault);
    setLexileMax(lexileMaxDefault);
    setLexile([lexileMinDefault, lexileMaxDefault]);
    setSearchTerm("");
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const lexileUnchanged =
      lexileMin === lexileMinDefault && lexileMax === lexileMaxDefault;
    const arUnchanged =
      acceleratedReaderMin === arMinDefault &&
      acceleratedReaderMax === arMaxDefault;

    const advancedSearchEvent: AdvancedSearchEvent = {
      searchString: {
        isReadingLevel: true,
        isAnd: true,
        searchTokens: [
          {
            searchString: searchTerm,
            type: SearchTypeKey.Contains,
            field: SearchFieldKey.AnyField,
          },
        ],
      },
      readingLevelFilters: {
        bookLevelStart: arUnchanged ? null : acceleratedReaderMin,
        bookLevelEnd: arUnchanged ? null : acceleratedReaderMax,
        interestLevel: interestLevel.trim().length === 0 ? null : interestLevel,
        lexileStart: lexileUnchanged ? null : lexileMin,
        lexileEnd: lexileUnchanged ? null : lexileMax,
      },
      branchFilters: selectedBranchIds,
    };

    onClickSearch(advancedSearchEvent);
  };

  React.useEffect(() => {
    axios
      .get<ReadingLevelInfo>(`/readingLevel/getInfo`)
      .then(({ data }) => {
        setInterestLevelList(data.interestLevelList);
      })
      .catch((error) => {
        console.log("Error fetching Reading Level Options");
      });
  }, []);

  // TODO loading state
  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack spacing={2} maxWidth="320px" width={"100%"}>
        <BranchLimitControl></BranchLimitControl>

        {pacSettings && pacSettings.lexileEnabled && (
          <React.Fragment>
            <Typography variant="h5">{translate("lexileMeasure")}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                label={translate("lexileMin")}
                value={lexileMin}
                size="small"
                onChange={handleMinimumChange}
                onBlur={handleMinimumBlur}
                sx={{ flexGrow: 1 }}
                inputProps={{
                  step: lexileStep,
                  min: lexileMinDefault,
                  max: lexileMaxDefault,
                  type: "number",
                }}
              />
              <Typography> - </Typography>
              <TextField
                sx={{ flexGrow: 1 }}
                label={translate("lexileMax")}
                value={lexileMax}
                size="small"
                onChange={handleMaximumChange}
                onBlur={handleMaximumBlur}
                inputProps={{
                  step: lexileStep,
                  min: lexileMinDefault,
                  max: lexileMaxDefault,
                  type: "number",
                }}
              />
            </Stack>
            <Box sx={{ padding: 1 }}>
              <Slider
                valueLabelDisplay="auto"
                getAriaLabel={(index) =>
                  index === 0 ? translate("lexileMin") : translate("lexileMax")
                }
                value={lexile}
                step={lexileStep}
                min={lexileMinDefault}
                onChange={handleSliderChange}
                max={lexileMaxDefault}
              ></Slider>
            </Box>
            <Divider></Divider>
          </React.Fragment>
        )}

        {pacSettings && pacSettings.acceleratedReaderEnabled && (
          <React.Fragment>
            <Typography variant="h5">{translate("atosLevel")}</Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                label={translate("arMin")}
                value={acceleratedReaderMin}
                size="small"
                sx={{ flexGrow: 1 }}
                onChange={handleARMinimumChange}
                inputProps={{
                  step: arStep,
                  inputMode: "decimal",
                  min: arMinDefault,
                  max: arMaxDefault,
                  type: "number",
                }}
              />
              <Typography> - </Typography>
              <TextField
                sx={{ flexGrow: 1 }}
                label={translate("arMax")}
                value={acceleratedReaderMax}
                onChange={handleARMaximumChange}
                size="small"
                inputProps={{
                  step: arStep,
                  min: arMinDefault,
                  max: arMaxDefault,
                  type: "number",
                }}
              />
            </Stack>

            <Box sx={{ padding: 1 }}>
              <Slider
                getAriaLabel={(index) =>
                  index === 0 ? translate("arMin") : translate("arMax")
                }
                valueLabelDisplay="auto"
                value={acceleratedReader}
                onChange={handleARSliderChange}
                step={arStep}
                min={arMinDefault}
                max={arMaxDefault}
              ></Slider>
            </Box>

            {interestLevelList && (
              <FormControl fullWidth>
                <InputLabel id="interest-level-label">
                  {translate("interestLevel")}
                </InputLabel>
                <Select
                  labelId="interest-level-label"
                  value={interestLevel}
                  label={translate("interestLevel")}
                  onChange={handleChange}
                >
                  {interestLevelList.map((interestLevelOption) => (
                    <MenuItem
                      value={interestLevelOption}
                      key={interestLevelOption}
                    >
                      {interestLevelOption}{" "}
                      {interestLevelHelpText[interestLevelOption]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!interestLevelList && <Skeleton></Skeleton>}
            <Divider></Divider>
          </React.Fragment>
        )}

        <TextField
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          label={translate("searchTerm")}
          size="small"
          helperText={translate("readingLevelSearchTermPlaceholder")}
        />

        <Stack direction="row" gap={2}>
          <Button type="submit" variant="contained">
            {translate("readingLevelSearchButton")}
          </Button>
          <Button type="button" variant="text" onClick={handleClear}>
            {translate("clear")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
